import { useState } from "react"
import useDidMount from "@/hooks/useDidMount"

import { WABAChannelMessageTemplate, WABATemplateCategory } from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"

import { ErrorType } from "@/hooks/useValidation"
import { usePermissionStateStore } from "@/store/PermissionState"

import ErrorHandlerService from "@/services/ErrorHandler"
import WABATemplateCreditsService, { WABATemplateCreditsInfo } from "@/@integrations/WABA/services/WABATemplateCreditsService"

import { UpsellDialog } from "@/components"

const useWABACredits = () => {
	const { checkPermission } = usePermissionStateStore()

	const [creditsInfo, setCreditsInfo] = useState<WABATemplateCreditsInfo>({
		totalCredits: 0,
		usedCredits: 0,
		availableCredits: 0
	})

	const [isWABATemplateCreditsLoading, setIsWABATemplateCreditsLoading] = useState(false)

	const userHasPermissionToAccessManagerPanel = checkPermission("manager-panel", "access")

	const getCreditCostByCategory = (category: WABATemplateCategory): number => {
		const categoryToCreditCost: Record<WABATemplateCategory, number> = {
			authentication: 1,
			marketing: 3,
			utility: 1,
			otp: 1
		}

		return categoryToCreditCost[category] ?? 1
	}

	const getCreditsInfo = async () => {
		setIsWABATemplateCreditsLoading(true)

		try {
			const response = await WABATemplateCreditsService.getCreditsInfo()

			setCreditsInfo(response)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setIsWABATemplateCreditsLoading(false)
	}

	const hasEnoughCreditsToSendTemplate = (template: WABAChannelMessageTemplate, sendQuantity: number): boolean => {
		const creditCost = getCreditCostByCategory(template.category) * sendQuantity

		return creditsInfo.availableCredits >= creditCost
	}

	const handleOpenTemplateSendLimitNearLimitDialog = () => {
		if (creditsInfo?.usedCredits >= creditsInfo?.totalCredits) {
			return
		}

		const WABA_TEMPLATE_CREDITS_NEAR_LIMIT_USAGE_RATIO = 0.9

		const usageRatio = creditsInfo?.usedCredits / creditsInfo?.totalCredits

		if (usageRatio >= WABA_TEMPLATE_CREDITS_NEAR_LIMIT_USAGE_RATIO) {
			UpsellDialog.open({
				dialogCode: userHasPermissionToAccessManagerPanel ? "template-send-limit-near-limit" : "template-send-limit-near-limit-attendant"
			})
		}
	}

	useDidMount(() => {
		getCreditsInfo()
	})

	return {
		isWABATemplateCreditsLoading,
		getCreditsInfo,
		creditsInfo,
		hasEnoughCreditsToSendTemplate,
		handleOpenTemplateSendLimitNearLimitDialog
	}
}

export default useWABACredits
